/**
 * Shared layout constants for the data-centers hub.
 */

import { breakpoints, layout } from "src/config/story-layout-constants";
import type { StoryThemeConfig } from "src/utils/story-theme-page-options";

/** ID of the skip-link target (slide stage). Used for id attribute and a11y root selector. */
export const SKIP_TARGET_ID = "content";

/**
 * Mosaic floor (title / chapter grids). When the viewport is shorter than this,
 * the hub stays in stacked (motion-off) mode instead of snap + inner scroll.
 *
 * `max-height: 47.5svw` is also a wide-aspect gate (~wider than 2.1:1). Height
 * uses `svw` (not bare `vh`/`dvh`). `min-width: 56rem` keeps phones out —
 * short / ultrawide desktop only. Live MQ in `useHubShortViewport` (no height
 * latch): URL-bar churn cannot match this query on phones anyway.
 */
export const MOSAIC_MIN_HEIGHT_VW = 47.5;
export const MOSAIC_MIN_HEIGHT = `${MOSAIC_MIN_HEIGHT_VW}svw`;
/** True when the window is too short / wide for a full mosaic — force stacked. */
export const HUB_SHORT_VIEWPORT_QUERY = `(max-height: ${MOSAIC_MIN_HEIGHT}) and (min-width: 56rem)`;

/**
 * Portrait / “phone stack” chapters vs landscape grid.
 *
 * Was `max-aspect-ratio: 1/1` (width ≤ height). Android Chrome’s URL bar
 * shrinks layout height and can flip that mid-stop — CSS then paints desktop
 * cells (black `revealBase` squares) over the mobile wipe, and JS used to
 * remount videos. `max-width: 100svh` is the same cut for normal phones
 * (portrait: width < svh; landscape: width > svh) but both axes are stable
 * across toolbar show/hide. `useHubNarrowViewport` latches against height
 * churn; it re-samples on real orientation change or layout-width change
 * (DevTools / preview size swap) so assets and wipe mode track the frame.
 *
 * Share-popover rules stay on a width query — that menu is sized for a narrow
 * width, including a phone in landscape.
 */
export const HUB_NARROW_MEDIA = "screen and (max-width: 100svh)";

/**
 * Narrow gap between a lockup and the block under it (title dek, Loudoun
 * dek/meta). The hed pads by the measured block plus this, so both slides
 * keep the same air when the copy wraps.
 */
export const HUB_HED_DEK_GAP = "1rem";

export { breakpoints, layout };

/**
 * Hub owns its PackageNav-style chrome; hide shared CSK minimal nav.
 */
export const storyTheme: StoryThemeConfig = {
  enabled: true,
  drawerSwitcher: false,
  minimalNav: false,
  navMode: "hidden",
};

/**
 * Client-side refresh for publish timestamps, comments, SEO, and analytics.
 */
export const refreshLiveMetadata = true;

export const sections = {
  credits: false,
} as const;

export const ads = {
  paragraphRunLength: 0,
} as const;
